import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Icon, SelectOption, Text } from '@factorialco/deprecated-design-system'
import {
  CategoryMapConfigFragment,
  CreateCategoryMapConfigDocument,
  CreateCategoryMapConfigItemDocument,
  DeleteCategoryMapConfigItemDocument,
  EducationLevelFragment,
  ProfessionalCategoryFragment,
  TrainingsCategoryMapConfigCategoryEnum,
  TrainingsCategoryMapConfigDocument,
  UpdateCategoryMapConfigItemDocument,
} from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  categoryMapConfigs: CategoryMapConfigFragment[]
  fields: EducationLevelFragment[] | ProfessionalCategoryFragment[]
  category: TrainingsCategoryMapConfigCategoryEnum | undefined
  closeModal: (value: boolean) => void
}

interface SubmitData {
  companyId: number
  fieldId: number
  fieldType: string
  value: string
}

export const CategoryConfigItemMapping = ({
  category,
  categoryMapConfigs,
  fields,
  closeModal,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const categoryMapConfig = categoryMapConfigs.find((cmp) => cmp.category === category)
  const fieldType =
    category === 'educationlevel'
      ? 'Contracts::Spain::EducationLevel'
      : 'Contracts::Spain::ProfessionalCategory'

  const [createCategoryMapConfig] = useMutation(CreateCategoryMapConfigDocument, {
    extractorFn: (data) => data.trainingsMutations.createTrainingsCategoryMapConfig,
  })

  const [createCategoryMapConfigItem] = useMutation(CreateCategoryMapConfigItemDocument, {
    extractorFn: (data) => data.trainingsMutations.createTrainingsCategoryMapConfigItem,
    refetchQueries: [TrainingsCategoryMapConfigDocument],
  })

  const [updateCategoryMapConfigItem] = useMutation(UpdateCategoryMapConfigItemDocument, {
    extractorFn: (data) => data.trainingsMutations.updateTrainingsCategoryMapConfigItem,
    refetchQueries: [TrainingsCategoryMapConfigDocument],
  })

  const [deleteCategoryMapConfigItem] = useMutation(DeleteCategoryMapConfigItemDocument, {
    extractorFn: (data) => data.trainingsMutations.deleteTrainingsCategoryMapConfigItem,
    refetchQueries: [TrainingsCategoryMapConfigDocument],
  })

  const onDelete = async (id: number) => {
    const result = await deleteCategoryMapConfigItem({ variables: { id: id } })

    handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    })
  }

  const onSubmit = async (data: SubmitData, id?: number) => {
    if (!categoryMapConfig && category) {
      const result = await createCategoryMapConfig({
        variables: { category: category, companyId: company.id },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      })

      if (handled.success) {
        onSubmitCategoryMapConfigSucceed({
          ...data,
          categoryMapConfigId: Number(handled.data.categoryMapConfig?.id),
        })
      }

      return
    }

    if (!id) {
      onSubmitCategoryMapConfigSucceed({
        ...data,
        categoryMapConfigId: Number(categoryMapConfig?.id),
      })

      return
    }

    onSubmitUpdateCategoryMapConfigItem(id, data.value)
  }

  const valueForField = (field: EducationLevelFragment | ProfessionalCategoryFragment) => {
    const f = categoryMapConfig?.categoryMapConfigItems.find(
      (cmp) => cmp.field.fieldId === field.id
    )

    return { value: f?.value, id: f?.id }
  }

  const onSubmitCategoryMapConfigSucceed = async (
    data: SubmitData & { categoryMapConfigId: number }
  ) => {
    const result = await createCategoryMapConfigItem({ variables: data })
    const response = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    })

    // We check length 1 as it's the last
    if (response.success && notMapped().length === 1) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.training_settings.fundae_subsidization.modal.all_mapped'),
      })
    }
  }

  const onSubmitUpdateCategoryMapConfigItem = async (id: number, value: string) => {
    const result = await updateCategoryMapConfigItem({ variables: { id: id, value: value } })
    handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    })
  }

  // These fields are given by fundae, we don't expose the enum from the backend so we need to mantain them here, consider exposing the enum from the backend
  const educationLevelFields: SelectOption[] = [
    { label: 'Educación postsecundaria no superior', value: 'educacionpostsecundarianosuperior' },
    { label: 'Educación primaria', value: 'educacionprimaria' },
    { label: 'Estudios universitarios primer ciclo', value: 'estudiosuniversitariosprimerciclo' },
    { label: 'Estudios universitarios segundo ciclo', value: 'estudiosuniversitariossegundociclo' },
    { label: 'Estudios universitarios tercer ciclo', value: 'estudiosuniversitariostercerciclo' },
    { label: 'Menos que primaria', value: 'menosqueprimaria' },
    { label: 'Otras titulaciones', value: 'otrastitulaciones' },
    { label: 'Primera etapa secundaria', value: 'primeraetapasecundaria' },
    { label: 'Segunda etapa secundaria', value: 'segundaetapasecundaria' },
    { label: 'Técnico superior', value: 'tecnicosuperior' },
  ]

  const professionalCategoryFields: SelectOption[] = [
    { label: 'Directivo', value: 'directivo' },
    { label: 'Mando intermedio', value: 'mandointermedio' },
    { label: 'Técnico', value: 'tecnico' },
    { label: 'Trabajador con baja cualificación', value: 'trabajadorconbajacualificacion' },
    { label: 'Trabajador cualificado', value: 'trabajadorcualificado' },
  ]

  const currentFields =
    category === 'educationlevel' ? educationLevelFields : professionalCategoryFields

  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => a.name.localeCompare(b.name)),
    [fields]
  )

  const categoryName =
    category === 'educationlevel'
      ? i18n.t('trainings.training_settings.fundae_subsidization.empty.education_level')
      : i18n.t('trainings.training_settings.fundae_subsidization.empty.professional_category')

  const options = currentFields.map((cf, index) => {
    return (
      <Selector.Option key={index} value={cf.value} label={cf.label} searchValue={String(cf.label)}>
        {cf.label}
      </Selector.Option>
    )
  })

  if (fields.length === 0) {
    return (
      <Modal
        title={i18n.t('trainings.training_settings.fundae_subsidization.empty.title', {
          categoryName,
        })}
        description={i18n.t('trainings.training_settings.fundae_subsidization.empty.description', {
          categoryName,
        })}
        illustration={<Icon.Large icon="edit-file-filled" color="iconCritical" />}
        footer={
          <>
            <Button
              onClick={() => {
                closeModal(false)
              }}
              type="secondary"
            >
              {i18n.t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                navigate(navigation.settings.contracts_settings.fullPath())
              }}
            >
              {i18n.t('trainings.training_settings.fundae_subsidization.empty.go_to_settings')}
            </Button>
          </>
        }
      ></Modal>
    )
  }

  return (
    <Modal
      description={i18n.t('trainings.training_settings.fundae_subsidization.modal.description')}
      illustration="window-user"
      title={
        category === 'educationlevel'
          ? i18n.t('trainings.training_settings.fundae_subsidization.modal.title_education_level')
          : i18n.t(
              'trainings.training_settings.fundae_subsidization.modal.title_professional_category'
            )
      }
      onClose={() => closeModal(false)}
    >
      <Box
        style={{ minWidth: '900px' }}
        paddingTop="s16"
        border={{ top: { color: 'grey200', width: 's1', style: 'solid' } }}
        gap="s24"
      >
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Box style={{ width: '47%' }}>
            <Text size="400" weight="semibold" color="grey1000">
              {i18n.t('trainings.training_settings.fundae_subsidization.modal.factorial_fields')}
            </Text>
          </Box>
          <Box style={{ width: '6%' }} />
          <Box style={{ width: '47%' }}>
            <Text size="400" weight="semibold" color="grey1000">
              {i18n.t('trainings.training_settings.fundae_subsidization.modal.fundae_fields')}
            </Text>
          </Box>
        </Box>
        {sortedFields.map((f) => (
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" key={f.id}>
            <Box style={{ width: '94%' }}>
              <Box>
                <Box
                  borderRadius={{ all: 'abs008' }}
                  border={{ all: { width: 's2', color: 'grey400', style: 'solid' } }}
                  background="grey200"
                  paddingX="s16"
                  paddingY="s8"
                  justifyContent="center"
                >
                  <Text ellipsis size="300">
                    {f.name}
                  </Text>
                </Box>
              </Box>
            </Box>
            <Box style={{ width: '12%' }} alignItems="center" justifyContent="center">
              <Icon.Medium color="grey400" icon="chevron-right"></Icon.Medium>
            </Box>
            <Box style={{ width: '94%' }}>
              <LabeledInput
                isLegend
                mandatory
                value={valueForField(f).value}
                completed={!!valueForField(f).value}
              >
                <Selector.Popover
                  isMultiSelect={false}
                  value={[valueForField(f).value]}
                  blankLabel=""
                  placeholder={i18n.t(
                    'trainings.training_settings.fundae_subsidization.modal.select'
                  )}
                  onChange={(value) => {
                    if (
                      value &&
                      value !==
                        i18n.t('trainings.training_settings.fundae_subsidization.modal.select')
                    ) {
                      const data = {
                        value: String(value),
                        fieldId: f.id,
                        fieldType: fieldType,
                        companyId: company.id,
                      }
                      onSubmit(data, valueForField(f).id)
                    } else {
                      const { id } = valueForField(f)

                      if (id) onDelete(id)
                    }
                  }}
                >
                  {options}
                </Selector.Popover>
              </LabeledInput>
            </Box>
          </Box>
        ))}
      </Box>
    </Modal>
  )

  function notMapped() {
    if (!categoryMapConfig) return fields
    const categoryMapConfigItemsFieldIds = categoryMapConfig.categoryMapConfigItems.map(
      (p) => p.field.fieldId
    )
    return fields.filter((field) => !categoryMapConfigItemsFieldIds.includes(field.id))
  }
}
